import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Bus,
  Car,
  CarFront,
  Info,
  Truck,
} from "lucide-react";
import type { Car as CarType } from "@/types/database";
import {
  getCarsByBodyChart,
  getCarsExplorerInsights,
  type ChartDatum,
  type PopularModelEntry,
} from "@/lib/analytics";
import { resolveCarMakeLogo } from "@/lib/car-make-logos";
import { GlassDonutChart } from "@/components/charts/GlassDonutChart";
import { FloatingModelLogos } from "@/components/cars/FloatingModelLogos";
import { MakeLogo } from "@/components/cars/MakeModelGrid";
import { Modal } from "@/components/ui/Modal";
import { useCarMakeLogos } from "@/lib/queries";
import { formatNumber } from "@/lib/utils";

function BodyTypeIcon({ bodyType, className }: { bodyType: string; className?: string }) {
  const normalized = bodyType.trim().toLowerCase();
  const iconClass = className ?? "h-7 w-7 text-accent-link";

  if (normalized.includes("suv") || normalized.includes("crossover")) {
    return <Truck className={iconClass} strokeWidth={2} />;
  }
  if (
    normalized.includes("truck") ||
    normalized.includes("pickup") ||
    normalized.includes("ute")
  ) {
    return <Truck className={iconClass} strokeWidth={2} />;
  }
  if (normalized.includes("van") || normalized.includes("minivan")) {
    return <Bus className={iconClass} strokeWidth={2} />;
  }
  if (
    normalized.includes("sedan") ||
    normalized.includes("saloon") ||
    normalized.includes("hatch")
  ) {
    return <CarFront className={iconClass} strokeWidth={2} />;
  }

  return <Car className={iconClass} strokeWidth={2} />;
}

function TopModelInsight({
  topModel,
  fleetSize,
}: {
  topModel: PopularModelEntry | null;
  fleetSize: number;
}) {
  const [infoOpen, setInfoOpen] = useState(false);
  const { data: makeLogos = new Map() } = useCarMakeLogos();
  const logo = topModel ? resolveCarMakeLogo(topModel.make, makeLogos) : null;
  const share =
    topModel && fleetSize > 0 ? Math.round((topModel.value / fleetSize) * 100) : 0;

  return (
    <>
      <div className="rounded-2xl bg-background/80 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted">
            Top model
          </div>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className="flex h-5 w-5 items-center justify-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="How top model is determined"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </div>

        {topModel ? (
          <div className="mt-2 flex items-center gap-2.5">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-canvas">
              <MakeLogo
                make={topModel.make || topModel.model}
                logo={logo}
                className="h-9 w-9 object-contain"
              />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-foreground">{topModel.model}</p>
              {topModel.make ? (
                <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-muted">
                  {topModel.make}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="mt-1 text-sm font-bold text-foreground">—</p>
        )}
      </div>

      <Modal open={infoOpen} onClose={() => setInfoOpen(false)} title="Top model">
        <div className="space-y-4 text-foreground">
          <p>
            The top model is the make and model combination that appears most often in your
            current Cars Explorer results.
          </p>
          <div className="rounded-2xl bg-canvas px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wider text-muted">How it&apos;s calculated</p>
            <ol className="mt-2 list-decimal space-y-2 pl-4 text-sm">
              <li>Start with the cars shown after your active search and filters.</li>
              <li>Group them by make and model (for example, Bentley + Continental).</li>
              <li>Count how many cars fall into each group.</li>
              <li>Rank groups from highest to lowest count — the leader is the top model.</li>
            </ol>
          </div>
          {topModel ? (
            <p>
              Right now,{" "}
              <span className="font-bold text-foreground">
                {topModel.label}
              </span>{" "}
              leads with{" "}
              <span className="font-bold text-foreground">
                {formatNumber(topModel.value)} {topModel.value === 1 ? "car" : "cars"}
              </span>
              {fleetSize > 0 ? (
                <>
                  {" "}
                  — about {share}% of the {formatNumber(fleetSize)} cars in view.
                </>
              ) : null}
            </p>
          ) : null}
          <p className="text-xs">
            If two models tie for the highest count, the first one in the ranked list is shown.
            Changing filters or search updates this instantly.
          </p>
        </div>
      </Modal>
    </>
  );
}

function TopBodyInsight({
  topBody,
  fleetSize,
}: {
  topBody: ChartDatum | null;
  fleetSize: number;
}) {
  const [infoOpen, setInfoOpen] = useState(false);
  const share = topBody && fleetSize > 0 ? Math.round((topBody.value / fleetSize) * 100) : 0;

  return (
    <>
      <div className="rounded-2xl bg-background/80 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted">
            Top type
          </div>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className="flex h-5 w-5 items-center justify-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="How top type is determined"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </div>

        {topBody ? (
          <div className="mt-2 flex items-center gap-2.5">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-canvas">
              <BodyTypeIcon bodyType={topBody.name} />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-foreground">{topBody.name}</p>
              <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-muted">
                Body type
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-1 text-sm font-bold text-foreground">—</p>
        )}
      </div>

      <Modal open={infoOpen} onClose={() => setInfoOpen(false)} title="Top type">
        <div className="space-y-4 text-foreground">
          <p>
            The top type is the body style that appears most often in your current Cars Explorer
            results.
          </p>
          <div className="rounded-2xl bg-canvas px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wider text-muted">How it&apos;s calculated</p>
            <ol className="mt-2 list-decimal space-y-2 pl-4 text-sm">
              <li>Start with the cars shown after your active search and filters.</li>
              <li>Group them by body type (for example, Sedan, SUV, or Coupe).</li>
              <li>Count how many cars fall into each group.</li>
              <li>Rank groups from highest to lowest count — the leader is the top type.</li>
            </ol>
          </div>
          {topBody ? (
            <p>
              Right now,{" "}
              <span className="font-bold text-foreground">{topBody.name}</span> leads with{" "}
              <span className="font-bold text-foreground">
                {formatNumber(topBody.value)} {topBody.value === 1 ? "car" : "cars"}
              </span>
              {fleetSize > 0 ? (
                <>
                  {" "}
                  — about {share}% of the {formatNumber(fleetSize)} cars in view.
                </>
              ) : null}
            </p>
          ) : null}
          <p className="text-xs">
            Cars without a body type are grouped as &quot;Unspecified.&quot; If two types tie for
            the highest count, the first one in the ranked list is shown. Changing filters or
            search updates this instantly.
          </p>
        </div>
      </Modal>
    </>
  );
}

export function CarsExplorerAnalytics({ cars }: { cars: CarType[] }) {
  const bodiesChart = useMemo(() => getCarsByBodyChart(cars), [cars]);
  const insights = useMemo(() => getCarsExplorerInsights(cars), [cars]);

  return (
    <div className="mb-5 overflow-hidden rounded-3xl bg-canvas">
      <div className="grid gap-0 lg:grid-cols-12">
        <div className="border-b border-border/40 p-4 sm:p-5 lg:col-span-3 lg:border-b-0 lg:border-r">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Fleet snapshot</p>
          <motion.div
            key={cars.length}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="mt-3"
          >
            <div className="flex items-end gap-2">
              <span className="text-[2.5rem] font-bold leading-none tracking-[-0.03em] text-foreground">
                {formatNumber(cars.length)}
              </span>
              <span className="pb-1 text-sm font-semibold text-muted">cars</span>
            </div>
            <p className="mt-2 text-xs text-muted">
              {formatNumber(insights.uniqueMakes)} makes · {formatNumber(insights.uniqueTypes)} types
            </p>
          </motion.div>

          <div className="mt-4 grid gap-2">
            <TopModelInsight topModel={insights.topModel} fleetSize={cars.length} />
            <TopBodyInsight topBody={insights.topBody} fleetSize={cars.length} />
          </div>
        </div>

        <div className="border-b border-border/40 p-4 sm:p-5 lg:col-span-5 lg:border-b-0 lg:border-r">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center text-accent-link">
              <Car className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Popular models</p>
              <p className="text-xs text-muted">Most represented in current results</p>
            </div>
          </div>
          <div className="h-[280px] overflow-hidden">
            <FloatingModelLogos cars={cars} />
          </div>
        </div>

        <div className="p-4 sm:p-5 lg:col-span-4">
          <div className="mb-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Body types</p>
            <p className="text-xs text-muted">Share of fleet by vehicle type</p>
          </div>
          <div className="h-[200px]">
            {bodiesChart.length > 0 ? (
              <GlassDonutChart data={bodiesChart} valueLabel="cars" maxSlices={5} />
            ) : (
              <p className="flex h-full items-center justify-center text-sm text-muted">
                No type data to chart.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
